import { isTokenExpired } from '@/@core/utils/auth.js';
import axios from 'axios';
import AuthService from '@/services/AuthService.js';

// Lazy-load router to break circular dependency:
// ApiClientFactory → router → ~pages ↔ pages → views → stores → services → ApiClientFactory
let _router = null;
async function getRouter() {
    if (!_router) {
        const mod = await import('@/router');
        _router = mod.default;
    }
    return _router;
}

// Track if a token refresh is already in progress (singleton promise)
let refreshPromise = null;

/**
 * Attempt to silently refresh the JWT token.
 * Returns the new token or null on failure.
 */
async function silentRefresh() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = AuthService.refreshToken()
        .then(response => {
            const newToken = response?.data?.token || response?.token;
            if (newToken) {
                localStorage.setItem('token', newToken);
                return newToken;
            }
            return null;
        })
        .catch(() => null)
        .finally(() => { refreshPromise = null; });

    return refreshPromise;
}

/**
 * Check if the token is about to expire (within 2 minutes).
 */
function isTokenExpiringSoon(token) {
    if (!token) return true;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        return (payload.exp - currentTime) < 120; // less than 2 minutes remaining
    } catch {
        return true;
    }
}

/**
 * Creates an Axios API client with:
 * - Automatic Bearer token injection
 * - Automatic x-org-id header injection (multi-tenancy)
 * - Silent token refresh before expiry
 * - 401 response interception with redirect to login
 */
export function createApiClient(baseURL) {

    const apiClient = axios.create({
        baseURL: baseURL,
        withCredentials: false,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        }
    });

    // ─── Request Interceptor ─────────────────────────────────────
    apiClient.interceptors.request.use(async config => {
        let token = localStorage.getItem('token');

        // If token is fully expired, redirect to login
        if (isTokenExpired(token)) {
            localStorage.removeItem('token');
            const router = await getRouter();
            router.push({ name: 'auth-login' });
            return Promise.reject(new Error('Token expired'));
        }

        // If token is expiring soon, attempt silent refresh
        if (isTokenExpiringSoon(token)) {
            const newToken = await silentRefresh();
            if (newToken) {
                token = newToken;
            }
        }

        // Inject Authorization header
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // ─── Multi-Tenancy: Inject x-org-id globally ────────────
        // Priority: explicit header > userData in localStorage
        if (!config.headers['x-org-id']) {
            const orgId = localStorage.getItem('org_id');
            if (orgId) {
                config.headers['x-org-id'] = orgId;
            }
        }

        // ─── Project Scoping: Inject x-project-id globally ────────
        // When a Project is selected, all API calls
        // are automatically scoped to that project's ID.
        if (!config.headers['x-project-id']) {
            const projectId = localStorage.getItem('selectedProjectId')
              || localStorage.getItem('selectedGroupId'); // legacy fallback
            if (projectId) {
                config.headers['x-project-id'] = projectId;
            }
        }

        return config;
    });

    // ─── Response Interceptor ────────────────────────────────────
    apiClient.interceptors.response.use(
        response => response,
        async error => {
            const status = error.response?.status;
            const originalRequest = error.config;

            // 401 Unauthorized — try silent refresh once before logging out
            if (status === 401 && !originalRequest._retried) {
                originalRequest._retried = true;
                const newToken = await silentRefresh();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return apiClient(originalRequest);
                }

                // Refresh failed — clear session and redirect to login
                localStorage.removeItem('token');
                const router = await getRouter();
                router.push({ name: 'auth-login' });
            }

            return Promise.reject(error);
        }
    );

    return apiClient;
}


